/**
 * Shop Settings Hooks
 *
 * TanStack Query hooks for shop settings management
 * Optimized for caching with appropriate refresh intervals
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getDefaultShopPriceApi, getShopDetailsApi, updateShopDetailsApi } from './shop.api';

// Query Keys for core shop domain
export const coreShopKeys = {
    all: ['core', 'shop'],
    defaultPrice: () => [...coreShopKeys.all, 'defaultPrice'],
    shopDetails: () => [...coreShopKeys.all, 'shopDetails'],
};

/**
 * Hook to get default shop price
 *
 * Uses optimized caching for shop price data:
 * - 5-minute cache (moderately changes)
 * - Auto refresh on window focus
 * - Background refetch for better UX
 *
 * @param {object} options - Query options
 * @returns {object} Query result with shop price data
 */
export const useGetDefaultShopPrice = (options = {}) => {
    return useQuery({
        queryKey: coreShopKeys.defaultPrice(),
        queryFn: getDefaultShopPriceApi,
        ...options,
    });
};

/**
 * Hook to get shop details
 *
 * Uses optimized caching for shop details:
 * - 10-minute cache (rarely changes)
 * - Auto refresh on window focus
 * - Background refetch for better UX
 *
 * @param {object} options - Query options
 * @returns {object} Query result with shop details data
 */
export const useGetShopDetails = (options = {}) => {
    return useQuery({
        queryKey: coreShopKeys.shopDetails(),
        queryFn: getShopDetailsApi,
        ...options,
    });
};

/**
 * Hook to update shop details
 *
 * Automatically invalidates and refetches shop details after update
 * Shows success/error notifications based on mutation result
 *
 * @param {object} options - Mutation options
 * @returns {object} Mutation result with update function
 */
export const useUpdateShopDetails = (options = {}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: updateShopDetailsApi,
        onSuccess: (data, variables, context) => {
            // Invalidate and refetch shop details
            queryClient.invalidateQueries({
                queryKey: coreShopKeys.shopDetails(),
            });

            // Call custom onSuccess if provided
            options.onSuccess?.(data, variables, context);
        },
        ...options,
    });
};
